import { useQueryClient } from "@tanstack/react-query";
import { AnalyticsComponentProvider, ButtonGroup, Flex, Link, Spinner, Text, StatusBanner } from "@wayflyer/flyui";
import type { BankConnectCountryConfig, BankingSettings } from "bankconnect";
import { useBankConnectFactory } from "bankconnect";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSpaNavigate, type BankConnectCountryConfig as BankButtonBankConnectCountryConfig } from "shared-ui";
import { handleContactTeam } from "shared-utils";
import { useCustomerAppConfig } from "undercarriage";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import {
  BANK_CONNECT_API,
  BANK_CONNECT_SOURCES_API,
  FUNDING_APPLICATION_CONFIGURATION_COMPLETION_STATUS_API,
} from "../../../components/constants";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { composePath } from "../../../hooks/navigation-next/use-funding-application-navigation-next";
import { useBankConnectProps, useBankingSources } from "../../../queries/bank-connect/queries";

import { BankConnectionList } from "./bank-connection-list/bank-connection-list";
import { ConfirmAccounts } from "./confirm-accounts/confirm-accounts";
import { BankConnectContextProvider } from "./context/bank-connect-provider";
import { CountrySelectionForm } from "./country-selection";
import { MESSAGES } from "./message-definitions";

const enum BANK_STATE {
  "confirm_accounts",
  "country_selection",
  "connection_list",
  "statement_upload",
  "loading",
}

type BankControllerProps = {
  currentPage?: PAGE_TYPE;
  statementsUploadPath?: string;
  requireConnection?: boolean;
};

export const BankController = ({
  currentPage = PAGE_TYPE.BANKING,
  statementsUploadPath = composePath(PAGE_TYPE.BANK_STATEMENTS_UPLOAD),
  requireConnection = false,
}: BankControllerProps) => {
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const {
    data: { bankConnectProps },
  } = useBankConnectProps(customerDomain);
  const { navigate } = useSpaNavigate("FundingApp");
  const queryClient = useQueryClient();
  const intl = useIntl();
  const navigationContext = useFundingApplicationNavigation({
    currentPage,
  });

  const { ready, useBankConnect } = useBankConnectFactory(bankConnectProps, {
    onErrorOrAbandon() {
      if (!requireConnection) {
        navigate(statementsUploadPath);
      }
    },
    onConnectionCallback() {
      // Refresh the bank sources and initial bank data for navigation on submit
      queryClient.invalidateQueries({
        queryKey: [BANK_CONNECT_SOURCES_API.replace(":customerSlug", customerDomain), customerDomain],
      });
      queryClient.invalidateQueries({
        queryKey: [BANK_CONNECT_API.replace(":customerSlug", customerDomain), customerDomain],
      });
      queryClient.invalidateQueries({
        queryKey: [FUNDING_APPLICATION_CONFIGURATION_COMPLETION_STATUS_API.replace(":customerSlug", customerDomain)],
      });
    },
  });

  const defaultCountry = bankConnectProps.countryOptions?.find(
    (c: BankConnectCountryConfig) => c.code === countryOfIncorporation
  );

  const shouldConfirmAccountSelection =
    bankConnectProps.basiqConnectProps.needsAccountSelection &&
    bankConnectProps.bankSettings.initialBankingData.bankingDataSources.length > 0;

  const { startConnect, bankConnections } = useBankConnect();
  const { isLoading, data: bankingSources } = useBankingSources(customerDomain);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasBankingConnections = !isLoading && bankingSources && !!bankingSources.bankingDataSources.length;

  const stepCompleted: boolean =
    hasBankingConnections || (!requireConnection && !!bankingSources.bankingStatementsGroups?.length);

  // eslint-disable-next-line require-await
  const onCountryConfirm = async (selection: BankConnectCountryConfig) => {
    if (selection.confirmEmail) {
      navigate(statementsUploadPath);
    }
    // the pydantic -> ts changes mean that the types are now incompatible
    return startConnect(selection as BankButtonBankConnectCountryConfig);
  };

  const currentState = determineScreenState(bankingSources, shouldConfirmAccountSelection);

  const [isAddAnotherFormVisible, setAddAnotherFormVisible] = useState(false);

  return (
    <AnalyticsComponentProvider component="bank-connect">
      {ready ? (
        <Flex direction="column" gap="8">
          {(bankingSources.bankingDataSources.length === 0 || !shouldConfirmAccountSelection) && (
            <FormattedMessage
              id="funding-application: bank connect - explainer text (generic)"
              defaultMessage="We use your banking data to assess the overall financial health of your business. This information forms an important part of our financial assessment."
            />
          )}
          {requireConnection && (
            <StatusBanner
              variant="info"
              subheading={
                <FormattedMessage
                  id="funding-application: bank connect - require connection explainer"
                  defaultMessage="<p>A live bank connection is required to submit your application. Connect your bank account to continue. Need help? <a>Contact support</a>.</p>"
                  values={{
                    p: (chunks: any) => <Text>{chunks}</Text>,
                    a: (chunks: any) => <Link onClick={handleContactTeam}>{chunks}</Link>,
                  }}
                />
              }
            />
          )}
          {!isSubmitting && (
            <BankConnectContextProvider bankConnectSchema={bankConnectProps}>
              {currentState === BANK_STATE.loading && (
                <Flex direction="column" gap="8">
                  <Text>{intl.formatMessage(MESSAGES.loadingBankData)}</Text>
                  <Spinner size={32} />
                </Flex>
              )}
              {currentState === BANK_STATE.confirm_accounts && (
                <ConfirmAccounts
                  bankConnections={bankConnections.filter((bankConnection) => !bankConnection.accountsSelected)}
                />
              )}
              {currentState === BANK_STATE.connection_list && (
                <BankConnectionList
                  formContent={
                    isAddAnotherFormVisible ? (
                      <Flex direction="column" width="100%" padding={["4", "0"]}>
                        <CountrySelectionForm
                          initialCountry={defaultCountry as BankButtonBankConnectCountryConfig}
                          onConfirm={(data) => onCountryConfirm(data).then(() => setAddAnotherFormVisible(false))}
                        />
                      </Flex>
                    ) : null
                  }
                  onAddAnother={() => setAddAnotherFormVisible(true)}
                  statementsUploadPath={statementsUploadPath}
                />
              )}
              {currentState === BANK_STATE.country_selection && (
                <CountrySelectionForm
                  initialCountry={defaultCountry as BankButtonBankConnectCountryConfig}
                  onConfirm={onCountryConfirm}
                />
              )}
            </BankConnectContextProvider>
          )}
          {!shouldConfirmAccountSelection &&
            // isSubmitting is intentionally NOT reset on success — the component unmounts
            // when the transition commits. Swapping the buttons for a spinner prevents the banking
            // content from appearing interactive while React's startTransition resolves the new route.
            (isSubmitting ? (
              <Flex justify="center">
                <Spinner size={32} />
              </Flex>
            ) : (
              <ButtonGroup
                primaryAction={{
                  onAction: async () => {
                    if (!navigationContext.nextPath) {
                      throw new Error("bank-connection-next-button: nextPath is not defined");
                    }
                    setIsSubmitting(true);
                    try {
                      await navigationContext.navigateWithStateUpdate(navigationContext.nextPath);
                    } catch (e) {
                      setIsSubmitting(false);
                      throw e;
                    }
                  },
                  content: intl.formatMessage(COMMON_MESSAGES.nextLabel),
                  disabled: !stepCompleted,
                  loading: isSubmitting,
                  analyticsId: "bank-connection-next-button",
                }}
                secondaryAction={
                  navigationContext.previousPath
                    ? {
                        onAction: () => {
                          if (navigationContext.previousPath) {
                            navigationContext.navigate(navigationContext.previousPath);
                          }
                        },
                        content: intl.formatMessage(COMMON_MESSAGES.backLabel),
                        analyticsId: "bank-connection-back-button",
                      }
                    : undefined
                }
                tertiaryAction={{
                  onAction: async () => {
                    if (!navigationContext.nextPath) {
                      throw new Error("bank-connection-skip-button: nextPath is not defined");
                    }
                    setIsSubmitting(true);
                    try {
                      await navigationContext.navigateWithStateUpdate(navigationContext.nextPath);
                    } catch (e) {
                      setIsSubmitting(false);
                      throw e;
                    }
                  },
                  content: intl.formatMessage(COMMON_MESSAGES.skipLabel),
                  disabled: stepCompleted,
                  loading: isSubmitting,
                  analyticsId: "bank-connection-skip-button",
                }}
                primaryActionTestId="navigation-continue-button"
                secondaryActionTestId="navigation-back-button"
                tertiaryActionTestId="navigation-skip-button"
              />
            ))}
        </Flex>
      ) : (
        <Flex direction="column" gap="6">
          <Text>
            {intl.formatMessage({
              id: "funding-application: bankconnect basiq launching",
              defaultMessage: "Connecting to Basiq, please wait",
            })}
          </Text>
          <Spinner size={32} />
        </Flex>
      )}
    </AnalyticsComponentProvider>
  );
};

function determineScreenState(
  bankingSources: BankingSettings | undefined,
  shouldConfirmAccountSelection: boolean
): BANK_STATE {
  const hasBankConnections = bankingSources && bankingSources.bankingDataSources.length > 0;
  const hasBankStatementGroups =
    bankingSources && bankingSources.bankingStatementsGroups && bankingSources.bankingStatementsGroups.length > 0;
  if (!bankingSources) {
    return BANK_STATE.loading;
  }
  if (hasBankConnections && shouldConfirmAccountSelection) {
    return BANK_STATE.confirm_accounts;
  }
  if ((hasBankConnections || hasBankStatementGroups) && !shouldConfirmAccountSelection) {
    return BANK_STATE.connection_list;
  }
  return BANK_STATE.country_selection;
}
